'use client'

/**
 * Purpose: Survey consent screen before camera capture — the required biometric
 *   analysis consent plus the separate, optional research-use opt-in.
 * Inputs: locale, consent state, callbacks.
 * Outputs: Renders both checkboxes and continue / questionnaire fallback; continue
 *   is gated on the biometric consent alone.
 */

import Link from 'next/link'
import { useTranslations } from 'next-intl'
import HoverGradientButton from '@/components/buttons/HoverGradientButton/HoverGradientButton'

export type SurveyCameraConsentProps = {
  locale: string
  imageConsent: boolean
  /** Optional research-use opt-in — unticked by default, never a precondition. */
  researchConsent: boolean
  onImageConsentChange: (value: boolean) => void
  onResearchConsentChange: (value: boolean) => void
  onContinue: () => void
  onUseQuestionnaire: () => void
}

export function SurveyCameraConsent({
  locale,
  imageConsent,
  researchConsent,
  onImageConsentChange,
  onResearchConsentChange,
  onContinue,
  onUseQuestionnaire,
}: SurveyCameraConsentProps) {
  const t = useTranslations('survey.photo')
  // Only the biometric-analysis consent gates the scan. Research use is a separate
  // decision the user is free to refuse without losing the feature.
  const consentGiven = imageConsent

  return (
    <div id="survey-camera-capture-consent">
      <h1 className="mb-2">{t('intro.title')}</h1>
      <p className="survey-camera-capture-description">{t('intro.description')}</p>
      <label className="survey-camera-capture-consent-row">
        <input
          id="survey-camera-consent-biometric"
          type="checkbox"
          checked={imageConsent}
          onChange={(e) => onImageConsentChange(e.target.checked)}
          className="survey-camera-capture-checkbox"
        />
        <span className="survey-camera-capture-consent-text">{t('consentBiometric')}</span>
      </label>
      {/* Optional and unticked by default; leaving it unchecked still scans. */}
      <label className="survey-camera-capture-consent-row-last">
        <input
          id="survey-camera-consent-research"
          type="checkbox"
          checked={researchConsent}
          onChange={(e) => onResearchConsentChange(e.target.checked)}
          className="survey-camera-capture-checkbox"
        />
        <span className="survey-camera-capture-consent-text">
          {t.rich('consentResearch', {
            termsLink: (chunks) => (
              <Link href={`/${locale}/terms`} className="survey-camera-capture-link" target="_blank" rel="noopener noreferrer">
                {chunks}
              </Link>
            ),
            privacyLink: (chunks) => (
              <Link href={`/${locale}/privacy`} className="survey-camera-capture-link" target="_blank" rel="noopener noreferrer">
                {chunks}
              </Link>
            ),
          })}
        </span>
      </label>
      <HoverGradientButton id="survey-camera-consent-continue" onClick={onContinue} disabled={!consentGiven} className="cursor-pointer disabled:opacity-50">
        {t('continue')}
      </HoverGradientButton>
      <HoverGradientButton
        id="survey-camera-use-questionnaire"
        variant="outline"
        onClick={onUseQuestionnaire}
        className="survey-camera-capture-secondary-action"
      >
        {t('useQuestionnaire')}
      </HoverGradientButton>
    </div>
  )
}
